import { Turnstile, type TurnstileInstance } from "@marsidev/react-turnstile";
import * as React from "react";
import { TURNSTILE_DEV_TOKEN, env, turnstileEnabled } from "@/lib/env";
import { useColorScheme } from "@/providers/theme-provider";

/**
 * The widget `action`, which must match the endpoint the token is spent on:
 *
 *   POST /v1/auth/signup           → signup
 *   POST /v1/auth/login            → login
 *   POST /v1/auth/forgot-password  → forgot-password
 */
export type TurnstileAction = "signup" | "login" | "forgot-password";

interface UseTurnstileResult {
  /** Null until the widget solves. Submit handlers should wait for it. */
  token: string | null;
  /** Render this next to the submit button. */
  widget: React.ReactNode;
  /** Tokens are single-use — call on every failure before a resubmit. */
  reset: () => void;
  required: boolean;
}

export function useTurnstile(action: TurnstileAction): UseTurnstileResult {
  const ref = React.useRef<TurnstileInstance | null>(null);
  const { resolved } = useColorScheme();
  const [token, setToken] = React.useState<string | null>(
    turnstileEnabled ? null : TURNSTILE_DEV_TOKEN,
  );

  const reset = React.useCallback(() => {
    if (!turnstileEnabled) return;
    setToken(null);
    ref.current?.reset();
  }, []);

  const widget = turnstileEnabled ? (
    <Turnstile
      ref={ref}
      siteKey={env.turnstileSiteKey}
      // A token is scoped to its action, so a login token can't be replayed
      // against signup.
      options={{ theme: resolved, size: "flexible", action, retry: "auto" }}
      onSuccess={setToken}
      onExpire={() => setToken(null)}
      onError={() => {
        setToken(null);
        // Locally the site key's hostname list usually won't include localhost,
        // which would leave the form permanently unsubmittable. An API with no
        // TURNSTILE_SECRET_KEY skips verification anyway, so fall through with
        // the placeholder rather than dead-ending the developer.
        if (env.isDev) {
          console.warn("[rocketeer] Turnstile failed to load — using the dev placeholder token.");
          setToken(TURNSTILE_DEV_TOKEN);
        }
      }}
      className="min-h-[65px]"
    />
  ) : null;

  return { token, widget, reset, required: turnstileEnabled };
}
